import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Globe, Instagram, Loader2, Send, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { PageHero, SectionLabel } from "@/components/Section";
import { CONTACT, IMAGES } from "@/data/content";
import { api } from "@/lib/api";

const inputCls =
  "w-full border-b-2 border-border bg-transparent px-1 py-2.5 text-espresso rounded-none focus:outline-none focus:border-rust transition-colors placeholder:text-muted-foreground/60";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Get In Touch"
        title="Contact"
        accent="Us"
        subtitle="Questions about sponsorship, registration, or the mission? We'd love to hear from you."
        image={IMAGES.vintageClubs}
      />

      <section className="paper-noise bg-background">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 relative z-10">
          <div className="lg:col-span-5">
            <SectionLabel>Reach Out</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Let's Connect</h2>
            <div className="mt-8 space-y-6">
              {[
                { icon: Mail, label: "Sponsorship", value: CONTACT.sponsorEmail },
                { icon: Phone, label: "Phone", value: CONTACT.sponsorPhone },
                { icon: ClipboardList, label: "Registration", value: "Register your team", to: "/register" },
                { icon: Globe, label: "Online", value: CONTACT.website },
                { icon: Instagram, label: "Social", value: CONTACT.social },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="h-11 w-11 bg-rust text-primary-foreground flex items-center justify-center"><c.icon size={18} /></span>
                  <div>
                    <div className="label-font text-[11px] text-rust">{c.label}</div>
                    {c.to ? (
                      <Link
                        to={c.to}
                        data-testid="contact-register-link"
                        className="text-espresso font-medium underline decoration-rust/50 underline-offset-4 transition-colors hover:text-rust"
                      >
                        {c.value} →
                      </Link>
                    ) : (
                      <div className="text-espresso font-medium">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="border border-border bg-card p-8 hard-shadow-sm">
              <h3 className="font-serif font-bold text-2xl text-espresso">Send A Message</h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Name *</label>
                  <input data-testid="contact-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                </div>
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Email *</label>
                  <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
                </div>
              </div>
              <div className="mt-6">
                <label className="label-font text-[11px] text-espresso/70">Subject</label>
                <input data-testid="contact-subject" className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?" />
              </div>
              <div className="mt-6">
                <label className="label-font text-[11px] text-espresso/70">Message *</label>
                <textarea data-testid="contact-message" rows={5} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message…" />
              </div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="mt-8 label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 hard-shadow-sm"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Message <Send size={15} /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
